import { Typography } from '@mui/material';
import { Fragment } from 'react';
import { formatLocation, formatMultiLineField, formatPartnerNames, formatEtdEta } from './rateManagementFormatters';
import { rateManagementCellTypographySx } from './rateManagementShared';

export const RateManagementDef = [
    {
        field: 'il_opus_reference_id',
        headerName: 'OPUS#',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{p.value ?? ''}</Typography>,
    },
    {
        field: 'il_trade_partner_id_client',
        headerName: 'Client',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatLocation(value) || value?.name || '',
        renderCell: (p) => (
            <Typography sx={rateManagementCellTypographySx}>
                {formatLocation(p.row.il_trade_partner_id_client) || p.row.il_trade_partner_id_client?.name || ''}
            </Typography>
        ),
    },
    {
        field: 'il_trade_partner_id_shipper',
        headerName: 'Shipper',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatPartnerNames(value),
        renderCell: (p) => {
            const text = formatPartnerNames(p.row.il_trade_partner_id_shipper);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'il_trade_partner_id_consignee',
        headerName: 'Consignee',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatPartnerNames(value),
        renderCell: (p) => {
            const text = formatPartnerNames(p.row.il_trade_partner_id_consignee);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'mbl',
        headerName: 'MBL/MAWB/BOL',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{p.value ?? ''}</Typography>,
    },
    {
        field: 'hbl',
        headerName: 'HBL/HAWB',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => {
            const text = formatMultiLineField(p.value);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'cntr_number',
        headerName: 'Container/Alias',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => {
            const text = formatMultiLineField(p.value);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'volume',
        headerName: 'Volume',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => {
            const text = formatMultiLineField(p.value);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'il_location_loading',
        headerName: 'POL/AOL',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatLocation(value, { nameOnly: true }),
        renderCell: (p) => (
            <Typography sx={rateManagementCellTypographySx}>
                {formatLocation(p.row.il_location_loading, { nameOnly: true })}
            </Typography>
        ),
    },
    {
        field: 'il_location_discharge',
        headerName: 'POD/AOD',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatLocation(value, { nameOnly: true }),
        renderCell: (p) => (
            <Typography sx={rateManagementCellTypographySx}>
                {formatLocation(p.row.il_location_discharge, { nameOnly: true })}
            </Typography>
        ),
    },
    {
        field: 'il_location_id_final_dest',
        headerName: 'F. DEST',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatLocation(value, { nameOnly: true }),
        renderCell: (p) => (
            <Typography sx={rateManagementCellTypographySx}>
                {formatLocation(p.row.il_location_id_final_dest, { nameOnly: true })}
            </Typography>
        ),
    },
    {
        field: 'il_location_id_deliver_to',
        headerName: 'Delivery Location',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatMultiLineField(value),
        renderCell: (p) => {
            const text = formatMultiLineField(p.row.il_location_id_deliver_to);
            return text ? (
                <Typography sx={rateManagementCellTypographySx}>
                    {text.split('\n').map((line, i) => (
                        <Fragment key={i}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                <Typography sx={rateManagementCellTypographySx} />
            );
        },
    },
    {
        field: 'quote_number',
        headerName: 'Quote Number',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{p.value ?? ''}</Typography>,
    },
    {
        field: 'gatein_date',
        headerName: 'Gate In Date',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{formatEtdEta(p.value)}</Typography>,
    },
    {
        field: 'etd',
        headerName: 'ETD',
        minWidth: 90,
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{formatEtdEta(p.value)}</Typography>,
    },
    {
        field: 'eta',
        headerName: 'ETA',
        minWidth: 90,
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => <Typography sx={rateManagementCellTypographySx}>{formatEtdEta(p.value)}</Typography>,
    },
];
